import { useState } from "react";
import { motion } from "framer-motion";
import { repairServices } from "@/data/repairServices";
import { ServiceCard } from "@/components/repair/ServiceCard";
import { RepairWhatsAppButton } from "@/components/whatsapp/WhatsAppButton";
import { Reveal, stagger } from "@/components/ui-motion/Reveal";
import { images } from "@/config/site";
import { useSeo } from "@/lib/seo";

const Field = ({ label, ...props }) => (
  <label className="block">
    <span className="mb-1.5 block text-sm font-medium">{label}</span>
    <input {...props} className="w-full rounded-xl border border-border bg-card px-4 py-3 text-sm outline-none transition-colors focus:border-[hsl(var(--ink))]" />
  </label>
);

export default function Repair() {
  useSeo({
    title: "Mobile & iPhone Repair in Rohini, Sector 3 — Mobile Specialist",
    description: "Professional mobile repair in Rohini: screen replacement, battery, charging port, camera, water damage, software and Apple repair. Get repair assistance on WhatsApp.",
    path: "/repair",
    image: images.repairScene,
  });
  const [form, setForm] = useState({ device: "", problem: "", name: "", phone: "" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const device = [form.device, form.name && `(${form.name}${form.phone ? `, ${form.phone}` : ""})`].filter(Boolean).join(" ");

  return (
    <div data-testid="repair-page">
      <section className="relative overflow-hidden border-b border-border">
        <div className="container-wide grid items-center gap-8 py-14 lg:grid-cols-2">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[hsl(var(--ink-soft))]">Professional Repair</p>
            <h1 className="mt-3 font-display text-4xl font-bold sm:text-5xl">Professional Mobile Repair in Rohini</h1>
            <p className="mt-4 max-w-lg text-[hsl(var(--ink-soft))]">Get your phone diagnosed and repaired by Mobile Specialist.</p>
            <RepairWhatsAppButton label="Get Repair Assistance" className="btn-accent mt-6" testId="repair-hero-whatsapp" />
          </div>
          <Reveal className="overflow-hidden rounded-3xl border border-border">
            <img src={images.repairScene} alt="Precision smartphone repair at Mobile Specialist" className="h-full w-full object-cover" loading="lazy" />
          </Reveal>
        </div>
      </section>

      <section className="container-wide py-16">
        <Reveal><h2 className="font-display text-3xl font-bold">What we repair</h2></Reveal>
        <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}
          className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {repairServices.map((s) => <ServiceCard key={s.id} service={s} />)}
        </motion.div>
        <p className="mt-6 text-sm text-[hsl(var(--ink-soft))]">Repair pricing and turnaround are shared after a quick diagnosis — message us with your device and issue.</p>
      </section>

      <section className="container-wide pb-8">
        <div className="grid gap-8 rounded-3xl border border-border bg-card p-8 lg:grid-cols-2 lg:p-12">
          <div>
            <h2 className="font-display text-3xl font-bold">Repair Enquiry</h2>
            <p className="mt-3 text-[hsl(var(--ink-soft))]">Tell us the device and problem. We'll open WhatsApp with your details ready to send — no forms lost, no waiting.</p>
          </div>
          <form className="space-y-4" onSubmit={(e) => e.preventDefault()} data-testid="repair-form">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Device Brand & Model" placeholder="e.g. iPhone 13" value={form.device} onChange={set("device")} data-testid="repair-device" />
              <Field label="Problem" placeholder="e.g. Screen damaged" value={form.problem} onChange={set("problem")} data-testid="repair-problem" />
              <Field label="Your Name" placeholder="Name" value={form.name} onChange={set("name")} data-testid="repair-name" />
              <Field label="Phone Number" placeholder="Mobile number" value={form.phone} onChange={set("phone")} data-testid="repair-phone" />
            </div>
            <RepairWhatsAppButton device={device} problem={form.problem} label="Get Repair Assistance" full testId="repair-submit" />
          </form>
        </div>
      </section>
    </div>
  );
}
